/**
 * 3D Room Visualizer — Opening Mesh Builder
 *
 * Procedural windows, doors and garage doors for the 3D Designer, built to the
 * size and position of each opening on its wall. No models, no bitmaps: every
 * part is generated from the opening's rectangle.
 *
 * Each kind comes in a few styles (openingStyles.ts) — a pattern in a set of
 * colours:
 *
 *   window      single-hung · picture · colonial grid
 *   door        six-panel · shaker · half-glass · flush
 *   garage door raised-panel · carriage · full-view glass
 *
 * What gives them depth. Seen head-on, a box standing proud of a door shows
 * only its face — the same colour as the door behind it — so a door built of
 * plain boxes reads as a flat drawing. Three things fix that:
 *   - BEVELS: raised parts are bevelled boxes (a face with four sloping sides).
 *     The slopes face up, down and sideways, so the light falls differently on
 *     each and the edge of every panel and rail is drawn by shading.
 *   - SHADE: each part carries a brightness in its vertex colours. Sunk fields,
 *     the cove round a raised panel and the undersides of bevels are darker —
 *     the shadow they would hold. It is baked in, so it shows in any light.
 *   - GRAIN: wooden styles get a procedural wood texture (woodTexture.ts), laid
 *     along each board the way it would run.
 *
 * Geometry is in canonical front-face coordinates, like boards: X along the
 * wall, Y up, Z out of the wall INTO the room (the wall plane is z = 0, the
 * outdoors is z < 0). The caller applies the per-face transform. Everything is
 * baked into the geometry (no mesh positions) because that transform mirrors
 * the left / right walls by rewriting vertices.
 *
 * Proportions are plausible rather than any particular manufacturer's.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { OpeningShape, WallFace } from './types';
import { DoorPattern, OpeningPalette, openingStyle } from './openingStyles';
import { applyWood, grainOffset, setGrainUVs } from './woodTexture';
import { GLASS_OPACITY, createGlassMaterial } from './materialFactory';
import { getProfile } from './profiles/profileRegistry';

export { GLASS_OPACITY };

const IN = 1 / 12;

interface Finish {
  color: number;
  roughness: number;
  /** < 1 makes the material see-through (window glass). */
  opacity?: number;
  metalness?: number;
  /** Wooden: gets the grain texture. */
  wood?: boolean;
  /** Glass: the shared glass material, whatever the other fields say. */
  glass?: boolean;
  /** What the export tags it (sceneExport.ts): a window or door frame and sill, or hardware. */
  kind?: 'frame' | 'hardware';
  /** The opening style this part belongs to (its id): the render's per-item texture placements go by it. */
  style?: string;
}

/**
 * Glass — the same everywhere: a window's panes, the light in a half-glass
 * door, the panes across a carriage door, the panels of a full-view one. Clear
 * enough to see out through; its tint, reflection and glare come from
 * materialFactory.createGlassMaterial (and the designer's render settings).
 * Nothing solid is ever built behind it.
 */
const GLASS: Finish = { color: 0xd3e6f0, opacity: GLASS_OPACITY, roughness: 0.06, glass: true };

const frame = (p: OpeningPalette): Finish => ({ color: p.frame, roughness: 0.45, kind: 'frame', style: p.id });
const sill = (p: OpeningPalette): Finish => ({ color: p.sill, roughness: 0.5, kind: 'frame', style: p.id });
const body = (p: OpeningPalette): Finish => ({ color: p.body, roughness: p.wood ? 0.62 : 0.5, wood: p.wood, style: p.id });
const panel = (p: OpeningPalette): Finish => ({ color: p.panel, roughness: p.wood ? 0.62 : 0.5, wood: p.wood, style: p.id });
const line = (p: OpeningPalette): Finish => ({ color: p.line, roughness: 0.6 });
const hardware = (p: OpeningPalette): Finish => ({ color: p.hardware, roughness: 0.3, metalness: 0.6, kind: 'hardware' });

// Baked shade: how much of the light a surface would really get.
const SHADE_FIELD = 0.82;       // a sunk field, between raised rails
const SHADE_COVE = 0.72;        // the groove round a raised panel
const SHADE_BEVEL_SIDE = 0.9;   // a slope facing sideways
const SHADE_BEVEL_DOWN = 0.74;  // a slope facing the floor

function materialFor(f: Finish): THREE.MeshStandardMaterial {
  if (f.glass) return createGlassMaterial();
  const opacity = f.opacity ?? 1;
  const material = new THREE.MeshStandardMaterial({
    color: f.color,
    roughness: f.roughness,
    metalness: f.metalness ?? 0,
    emissive: f.color,
    emissiveIntensity: opacity < 1 ? 0.1 : 0.28,
    vertexColors: true,
    side: THREE.DoubleSide,
    transparent: opacity < 1,
    opacity,
    depthWrite: opacity >= 1,
  });
  if (f.wood) {
    applyWood(material, 'fine', 0.5);
    material.userData.kind = 'doorWood'; // for the designer's render settings (relief)
  } else if (f.kind) {
    material.userData.kind = f.kind; // frames and sills, hardware: their own kinds in the export (sceneExport.ts), for the render's finishes
  }
  if (f.style && (f.wood || f.kind === 'frame')) material.userData.style = f.style; // the export tags the part with its style: the render's per-item placements
  // The wall fade multiplies this rather than replacing it, so glass stays glass
  // (see transparencyController.applyOpacityToGroup).
  material.userData.baseOpacity = opacity;
  return material;
}

interface PartOptions {
  /** Which way the grain runs (wooden finishes). Default: up the part. */
  grain?: 'x' | 'y';
  /** Baked brightness, 0..1. Default 1. */
  shade?: number;
}

/** A flat-shaded mesh from quads: each quad its own four vertices, normal and shade. */
class QuadBuilder {
  private positions: number[] = [];
  private normals: number[] = [];
  private colors: number[] = [];
  private indices: number[] = [];

  /** Corners counter-clockwise as seen from the side the quad faces. */
  quad(a: number[], b: number[], c: number[], d: number[], shade: number): void {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const vx = d[0] - a[0], vy = d[1] - a[1], vz = d[2] - a[2];
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    const o = this.positions.length / 3;
    for (const p of [a, b, c, d]) {
      this.positions.push(p[0], p[1], p[2]);
      this.normals.push(nx, ny, nz);
      this.colors.push(shade, shade, shade);
    }
    this.indices.push(o, o + 1, o + 2, o, o + 2, o + 3);
  }

  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    g.setIndex(this.indices);
    return g;
  }
}

/** A quad facing away from `centre` (corners in either order). */
function faceOut(q: QuadBuilder, a: number[], b: number[], c: number[], d: number[], centre: number[], shade: number): void {
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
  const vx = d[0] - a[0], vy = d[1] - a[1], vz = d[2] - a[2];
  const n = [uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx];
  const m = [(a[0] + c[0]) / 2 - centre[0], (a[1] + c[1]) / 2 - centre[1], (a[2] + c[2]) / 2 - centre[2]];
  if (n[0] * m[0] + n[1] * m[1] + n[2] * m[2] < 0) q.quad(a, d, c, b, shade);
  else q.quad(a, b, c, d, shade);
}

/** Collects parts by finish, then merges each finish into one mesh. */
class Parts {
  private byFinish = new Map<string, { finish: Finish; geoms: THREE.BufferGeometry[] }>();
  /** When set, everything added is cut to this band of heights (one section of a garage door). */
  clipY: [number, number] | null = null;

  private add(finish: Finish, g: THREE.BufferGeometry, x: number, y: number, options: PartOptions): void {
    setGrainUVs(g, options.grain ?? 'y', grainOffset(x, y) * 97);
    // The same finish on two openings (or two styles that share a colour) is one mesh.
    const key = `${finish.color}:${finish.roughness}:${finish.opacity ?? 1}:${finish.metalness ?? 0}:${finish.wood ? 1 : 0}`;
    if (!this.byFinish.has(key)) this.byFinish.set(key, { finish, geoms: [] });
    this.byFinish.get(key)!.geoms.push(g);
  }

  /** A box spanning [x0,x1] × [y0,y1] × [z0,z1]. Degenerate boxes are ignored. */
  box(finish: Finish, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, options: PartOptions = {}): void {
    this.bevel(finish, x0, x1, y0, y1, z0, z1, 0, options);
  }

  /**
   * A box whose room-side face is smaller than its base by `bevel` all round,
   * with four sloping sides between them — a raised panel, a rail, a board.
   * With no bevel it is a plain box (its sides square to the face).
   */
  bevel(finish: Finish, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, bevel: number, options: PartOptions = {}): void {
    if (this.clipY) {
      y0 = Math.max(y0, this.clipY[0]);
      y1 = Math.min(y1, this.clipY[1]);
    }
    const w = x1 - x0, h = y1 - y0, d = z1 - z0;
    if (w <= 1e-4 || h <= 1e-4 || d <= 1e-4) return;
    const b = Math.max(0, Math.min(bevel, w / 2 - 1e-4, h / 2 - 1e-4));
    const shade = options.shade ?? 1;
    const q = new QuadBuilder();
    // Face, toward the room.
    q.quad([x0 + b, y0 + b, z1], [x1 - b, y0 + b, z1], [x1 - b, y1 - b, z1], [x0 + b, y1 - b, z1], shade);
    // Sides: bottom (faces down), right, top (faces up), left.
    q.quad([x0, y0, z0], [x1, y0, z0], [x1 - b, y0 + b, z1], [x0 + b, y0 + b, z1], shade * SHADE_BEVEL_DOWN);
    q.quad([x1, y0, z0], [x1, y1, z0], [x1 - b, y1 - b, z1], [x1 - b, y0 + b, z1], shade * SHADE_BEVEL_SIDE);
    q.quad([x1, y1, z0], [x0, y1, z0], [x0 + b, y1 - b, z1], [x1 - b, y1 - b, z1], shade);
    q.quad([x0, y1, z0], [x0, y0, z0], [x0 + b, y0 + b, z1], [x0 + b, y1 - b, z1], shade * SHADE_BEVEL_SIDE);
    // Back, toward the outdoors (seen through a faded wall, or from outside).
    q.quad([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], shade);
    this.add(finish, q.build(), x0, y0, options);
  }

  /**
   * A prism along x (from x0 to x1) of a four-cornered outline in the y–z plane
   * — a length of track round a curve, a brace, anything not square to the axes.
   */
  prism(finish: Finish, x0: number, x1: number, outline: [number, number][], options: PartOptions = {}): void {
    const shade = options.shade ?? 1;
    const q = new QuadBuilder();
    const cy = outline.reduce((s, p) => s + p[0], 0) / outline.length;
    const cz = outline.reduce((s, p) => s + p[1], 0) / outline.length;
    const centre = [(x0 + x1) / 2, cy, cz];
    const at = (x: number, p: [number, number]) => [x, p[0], p[1]];
    const n = outline.length;
    faceOut(q, at(x0, outline[0]), at(x0, outline[1]), at(x0, outline[2]), at(x0, outline[3 % n]), centre, shade);
    faceOut(q, at(x1, outline[0]), at(x1, outline[1]), at(x1, outline[2]), at(x1, outline[3 % n]), centre, shade);
    for (let i = 0; i < n; i++) {
      const a = outline[i], b = outline[(i + 1) % n];
      faceOut(q, at(x0, a), at(x0, b), at(x1, b), at(x1, a), centre, shade);
    }
    this.add(finish, q.build(), x0, cy, options);
  }

  /**
   * A slab from z0 to z1 of a convex outline in the x–y plane (corners in
   * order) — a board lying on a door's face at any angle: a cross brace. Cut
   * to the clip band like everything else.
   */
  slab(finish: Finish, outline: [number, number][], z0: number, z1: number, options: PartOptions = {}): void {
    let poly = outline;
    if (this.clipY) {
      for (const [lo, keepAbove] of [[this.clipY[0], true], [this.clipY[1], false]] as const) {
        const inside = (p: [number, number]) => (keepAbove ? p[1] >= lo : p[1] <= lo);
        const out: [number, number][] = [];
        for (let i = 0; i < poly.length; i++) {
          const a = poly[i], b = poly[(i + 1) % poly.length];
          const ia = inside(a), ib = inside(b);
          if (ia) out.push(a);
          if (ia !== ib) {
            const t = (lo - a[1]) / (b[1] - a[1]);
            out.push([a[0] + (b[0] - a[0]) * t, lo]);
          }
        }
        poly = out;
        if (poly.length < 3) return;
      }
    }
    if (z1 - z0 <= 1e-4) return;
    const shade = options.shade ?? 1;
    const q = new QuadBuilder();
    const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
    const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
    const centre = [cx, cy, (z0 + z1) / 2];
    const at = (p: [number, number], z: number) => [p[0], p[1], z];
    // Face and back as fans from the first corner; the sides as quads.
    for (let i = 1; i + 1 < poly.length; i++) {
      faceOut(q, at(poly[0], z1), at(poly[i], z1), at(poly[i + 1], z1), at(poly[i + 1], z1), centre, shade);
      faceOut(q, at(poly[0], z0), at(poly[i], z0), at(poly[i + 1], z0), at(poly[i + 1], z0), centre, shade);
    }
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length];
      faceOut(q, at(a, z0), at(b, z0), at(b, z1), at(a, z1), centre, shade * SHADE_BEVEL_SIDE);
    }
    this.add(finish, q.build(), cx, cy, options);
  }

  /** A round bar along x (faceted), centred on (y, z) — a shaft, a spring, a drum. */
  cylinderX(finish: Finish, x0: number, x1: number, y: number, z: number, radius: number, facets = 12, options: PartOptions = {}): void {
    if (x1 - x0 <= 1e-4 || radius <= 1e-4) return;
    const shade = options.shade ?? 1;
    const q = new QuadBuilder();
    const centre = [(x0 + x1) / 2, y, z];
    const ring = Array.from({ length: facets }, (_, i) => {
      const t = (i / facets) * Math.PI * 2;
      return [y + radius * Math.cos(t), z + radius * Math.sin(t)] as [number, number];
    });
    for (let i = 0; i < facets; i++) {
      const a = ring[i], b = ring[(i + 1) % facets];
      faceOut(q, [x0, a[0], a[1]], [x0, b[0], b[1]], [x1, b[0], b[1]], [x1, a[0], a[1]], centre, shade);
      // The ends, as thin wedges from the axis.
      faceOut(q, [x0, y, z], [x0, a[0], a[1]], [x0, b[0], b[1]], [x0, y, z], centre, shade);
      faceOut(q, [x1, y, z], [x1, a[0], a[1]], [x1, b[0], b[1]], [x1, y, z], centre, shade);
    }
    this.add(finish, q.build(), x0, y, options);
  }

  /**
   * A deep part with just its room-side edges eased: square sides, then a 45°
   * chamfer of `ease` at the face. (A bevel over a part's whole depth would be
   * a steep slope, all but edge-on to the room — it shades as a line of noise.)
   */
  eased(finish: Finish, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, ease: number, options: PartOptions = {}): void {
    const e = Math.max(0, Math.min(ease, (z1 - z0) / 2, (x1 - x0) / 3, (y1 - y0) / 3));
    this.bevel(finish, x0, x1, y0, y1, z0, z1 - e, 0, options);
    this.bevel(finish, x0, x1, y0, y1, z1 - e, z1, e, options);
  }

  into(group: THREE.Group): void {
    for (const { finish, geoms } of this.byFinish.values()) {
      const merged = mergeGeometries(geoms, false);
      geoms.forEach((g) => g.dispose());
      if (!merged) continue;
      const mesh = new THREE.Mesh(merged, materialFor(finish));
      mesh.castShadow = (finish.opacity ?? 1) >= 1;
      // Not shadowed by the room's lamps: their shadow maps are far too coarse
      // for parts this small, and on the little sloping faces they show as a
      // peppering of dots. The relief is carried by the bevels and baked shade.
      mesh.receiveShadow = false;
      group.add(mesh);
    }
  }
}

// ============================================================================
// MOVING PARTS — a door leaf that swings, a sash that lifts, garage sections
// ============================================================================

/**
 * How a moving part moves, in canonical front-face coordinates; `amount` runs
 * from 0 (closed) to 1 (open).
 *   swing — about a vertical line through (hinge x, hinge z), a quarter turn;
 *           `sign` −1 turns the +x side toward the room (+z).
 */
export type Movement =
  | { kind: 'swing'; hinge: [number, number]; sign: 1 | -1 }
  /** lift — straight up by `by` feet (a window's lower sash). */
  | { kind: 'lift'; by: number }
  /**
   * roll — one section of a garage door, from `y0` to `y1`, riding a track that
   * runs up the opening (its room face at z = `z`) to `top`, curves over (radius
   * `r`) and runs straight back into the room under the ceiling; open, the
   * whole door (`travel` feet) has gone round the curve. The door hangs
   * `clear` feet in front of where it is drawn — open or closed — so that it
   * rides wholly in front of the wall, clear of the wall over the opening as it
   * rounds the curve, as a real door hangs inside the garage.
   */
  | { kind: 'roll'; y0: number; y1: number; top: number; z: number; r: number; travel: number; clear: number }
  /** fixed — does not move (a garage door's opener button: only clicked). */
  | { kind: 'fixed' };

/** Where a moving part is at `amount`, as a matrix in canonical coordinates. */
export function movementMatrix(m: Movement, amount: number, out = new THREE.Matrix4()): THREE.Matrix4 {
  const t = Math.max(0, Math.min(1, amount));
  switch (m.kind) {
    case 'swing': {
      const [hx, hz] = m.hinge;
      const turn = new THREE.Matrix4().makeRotationY(m.sign * t * (Math.PI / 2));
      return out.makeTranslation(hx, 0, hz).multiply(turn).multiply(new THREE.Matrix4().makeTranslation(-hx, 0, -hz));
    }
    case 'lift':
      return out.makeTranslation(0, t * m.by, 0);
    case 'roll': {
      // The point `u` feet along the track from the floor: up, round the curve,
      // then back into the room.
      // The door always rides on the tracks' line, `clear` in front of where it
      // is drawn — closed too, so there is no step out from the wall.
      const d = t * m.travel;
      const z = m.z + m.clear;
      const along = (u: number): [number, number] => {
        if (u <= m.top) return [u, z];
        const turn = (u - m.top) / m.r;
        if (turn <= Math.PI / 2) return [m.top + m.r * Math.sin(turn), z + m.r * (1 - Math.cos(turn))];
        return [m.top + m.r, z + m.r + (u - m.top - (Math.PI / 2) * m.r)];
      };
      const [by, bz] = along(m.y0 + d);
      const [ty, tz] = along(m.y1 + d);
      // Bottom edge on the track, top edge toward the next point on it (round the
      // corner it cuts across, as a real section does).
      const tilt = Math.atan2(tz - bz, ty - by);
      return out.makeTranslation(0, by, bz)
        .multiply(new THREE.Matrix4().makeRotationX(tilt))
        .multiply(new THREE.Matrix4().makeTranslation(0, -m.y0, -m.z));
    }
    case 'fixed':
      return out.identity();
  }
}

/**
 * The map from canonical front-face coordinates to the room's, for the faces
 * whose geometry is rewritten vertex by vertex (left, right). Front and back
 * get theirs from the group's own transform, so for them it is the identity.
 */
export function faceMatrix(face: WallFace, roomLength: number, roomWidth: number): THREE.Matrix4 {
  const m = new THREE.Matrix4();
  if (face === 'left') m.set(0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1);
  if (face === 'right') m.set(0, 0, -1, roomLength, 0, 1, 0, 0, -1, 0, 0, roomWidth, 0, 0, 0, 1);
  return m;
}

/**
 * Tag the moving parts of a wall's openings with the face map, once the face
 * transform has been applied — the viewer moves them with `placeMovingPart`.
 */
export function tagMovingParts(openings: THREE.Group, face: WallFace, roomLength: number, roomWidth: number): void {
  const f = faceMatrix(face, roomLength, roomWidth);
  openings.traverse((child) => {
    if (child.userData.movement || child.userData.garageRig) child.userData.faceMatrix = f;
  });
}

/** Put a moving part where it is at `amount` (0 closed … 1 open). */
export function placeMovingPart(part: THREE.Object3D, amount: number): void {
  const movement = part.userData.movement as Movement | undefined;
  if (!movement) return;
  const f = (part.userData.faceMatrix as THREE.Matrix4 | undefined) ?? new THREE.Matrix4();
  part.matrixAutoUpdate = false;
  part.matrix.copy(f).multiply(movementMatrix(movement, amount)).multiply(f.clone().invert());
  part.matrixWorldNeedsUpdate = true;
}

/** An invisible box that is what you click to open or close something. */
function handleTarget(x0: number, x1: number, y0: number, y1: number, z0: number, z1: number): THREE.Mesh {
  const g = new THREE.BoxGeometry(x1 - x0, y1 - y0, z1 - z0);
  g.translate((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
  const mesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ visible: false }));
  mesh.userData.openerHandle = true;
  mesh.userData.skipAO = true;
  return mesh;
}

/** A group for one moving part, with what it is and how it moves. */
function movingPart(key: string, movement: Movement, parts: Parts, handle?: THREE.Mesh): THREE.Group {
  const group = new THREE.Group();
  group.name = 'moving-part';
  group.userData.openerKey = key;
  group.userData.movement = movement;
  parts.into(group);
  if (handle) group.add(handle);
  return group;
}

// How far things sit relative to the wall plane (z = 0), in feet.
const Z_OUT = -3.5 * IN;   // outside face of the wall framing
const Z_PROUD = 0.9 * IN;  // just proud of the boards on the room side

/** A raised panel: a dark cove round it, then the panel itself with a broad bevel. */
function raisedPanel(p: Parts, finish: Finish, x0: number, x1: number, y0: number, y1: number, z: number, proud: number, options: PartOptions = {}): void {
  const cove = 0.45 * IN;
  p.box(finish, x0 - cove, x1 + cove, y0 - cove, y1 + cove, z, z + 0.03 * IN, { ...options, shade: SHADE_COVE });
  p.bevel(finish, x0, x1, y0, y1, z + 0.03 * IN, z + proud, Math.min(0.8 * IN, (x1 - x0) / 5, (y1 - y0) / 5), options);
}

// ============================================================================
// WINDOWS
// ============================================================================

function addWindow(p: Parts, o: OpeningShape, root?: THREE.Group): void {
  const { pattern, palette } = openingStyle('window', o.style);
  const { x, y, width: w, height: h } = o;
  const f = Math.min(2.25 * IN, w / 6, h / 6); // frame face width
  const x1 = x + w, y1 = y + h;
  const fr = frame(palette);

  // Frame — a box section round the opening, the depth of the wall, its room
  // side eased with a small bevel.
  const edge = 0.3 * IN;
  p.eased(fr, x, x + f, y, y1, Z_OUT, Z_PROUD, edge);
  p.eased(fr, x1 - f, x1, y, y1, Z_OUT, Z_PROUD, edge);
  p.eased(fr, x + f, x1 - f, y1 - f, y1, Z_OUT, Z_PROUD, edge, { grain: 'x' });
  p.eased(fr, x + f, x1 - f, y, y + f, Z_OUT, Z_PROUD, edge, { grain: 'x' });

  // Glass, set back in the frame. (A hung window's lower pane is its sash's own,
  // below.)
  const gz = Z_OUT * 0.45;
  const gx0 = x + f, gx1 = x1 - f, gy0 = y + f, gy1 = y1 - f;
  const hungFamily = pattern === 'hung' || pattern === 'traditional-hung' || pattern === 'prairie-hung' || pattern === 'grid-hung';
  const opens = hungFamily && h > 2.5 && !!root;
  const mid = y + h / 2;
  if (!opens) p.box(GLASS, gx0, gx1, gy0, gy1, gz - 0.3 * IN, gz + 0.3 * IN);

  // The sash: a slimmer frame round the glass, set back from the outer one —
  // a second step in, which is most of what makes a window look built.
  const sash = Math.min(1.5 * IN, f);
  const sz0 = gz - 0.7 * IN, sz1 = gz + 1.1 * IN;
  const sashAround = (ax0: number, ax1: number, ay0: number, ay1: number, into: Parts = p, dz = 0) => {
    into.eased(fr, ax0, ax0 + sash, ay0, ay1, sz0 + dz, sz1 + dz, 0.25 * IN, { shade: 0.96 });
    into.eased(fr, ax1 - sash, ax1, ay0, ay1, sz0 + dz, sz1 + dz, 0.25 * IN, { shade: 0.96 });
    into.eased(fr, ax0 + sash, ax1 - sash, ay1 - sash, ay1, sz0 + dz, sz1 + dz, 0.25 * IN, { grain: 'x', shade: 0.96 });
    into.eased(fr, ax0 + sash, ax1 - sash, ay0, ay0 + sash, sz0 + dz, sz1 + dz, 0.25 * IN, { grain: 'x', shade: 0.96 });
  };
  // A bar across a pane, standing a little proud of its glass. `into` / `dz`:
  // which sash it belongs to and how far forward that sash sits.
  const bar = (thick: number, vertical: boolean, at: number, b0: number, b1: number, into: Parts = p, dz = 0) => (vertical
    ? into.eased(fr, at - thick / 2, at + thick / 2, b0, b1, gz - 0.5 * IN + dz, gz + 0.8 * IN + dz, thick / 4)
    : into.eased(fr, b0, b1, at - thick / 2, at + thick / 2, gz - 0.5 * IN + dz, gz + 0.8 * IN + dz, thick / 4, { grain: 'x' }));

  // ---- The grille patterns, each drawn over one pane (a sash's glass, inside its frame).
  const thick = Math.min(0.75 * IN, f);
  /** Colonial grilles: panes about a foot square. */
  const grid = (ax0: number, ax1: number, ay0: number, ay1: number, into: Parts = p, dz = 0) => {
    const cols = Math.max(2, Math.round((ax1 - ax0) / 1.1));
    const rows = Math.max(2, Math.round((ay1 - ay0) / 1.2));
    for (let c = 1; c < cols; c++) bar(thick, true, ax0 + ((ax1 - ax0) * c) / cols, ay0, ay1, into, dz);
    for (let r = 1; r < rows; r++) bar(thick, false, ay0 + ((ay1 - ay0) * r) / rows, ax0, ax1, into, dz);
  };
  /** Prairie: one bar in from each side, framing a clear centre with small panes at the corners. */
  const prairie = (ax0: number, ax1: number, ay0: number, ay1: number, into: Parts = p, dz = 0) => {
    const inset = Math.min(5 * IN, (ax1 - ax0) / 4, (ay1 - ay0) / 4);
    bar(thick, true, ax0 + inset, ay0, ay1, into, dz);
    bar(thick, true, ax1 - inset, ay0, ay1, into, dz);
    bar(thick, false, ay0 + inset, ax0, ax1, into, dz);
    bar(thick, false, ay1 - inset, ax0, ax1, into, dz);
  };
  /** Fractional: a row of small panes across the top, the rest clear. */
  const fractional = (ax0: number, ax1: number, ay0: number, ay1: number, into: Parts = p, dz = 0) => {
    const rowH = Math.min(10 * IN, (ay1 - ay0) / 3.5);
    const cols = Math.max(2, Math.round((ax1 - ax0) / 0.8));
    bar(thick, false, ay1 - rowH, ax0, ax1, into, dz);
    for (let c = 1; c < cols; c++) bar(thick, true, ax0 + ((ax1 - ax0) * c) / cols, ay1 - rowH, ay1, into, dz);
  };
  /** Diamond: a lattice of bars on both diagonals, each cut to the pane. */
  const diamond = (ax0: number, ax1: number, ay0: number, ay1: number, into: Parts = p, dz = 0) => {
    const dw = 7 * IN, dh = 10.5 * IN; // one diamond, across and up
    const slope = dh / dw;
    const z0 = gz - 0.5 * IN + dz, z1 = gz + 0.8 * IN + dz;
    // Lines y = y0 + slope * (x - ax0) + k * dh (rising) and y = y0 - slope * (x - ax0) + k * dh (falling), cut to the pane.
    for (const sign of [1, -1]) {
      const kMin = Math.floor(-(ay1 - ay0 + slope * (ax1 - ax0)) / dh) - 1, kMax = Math.ceil((ay1 - ay0 + slope * (ax1 - ax0)) / dh) + 1;
      for (let k = kMin; k <= kMax; k++) {
        const yAt = (xx: number) => ay0 + sign * slope * (xx - ax0) + k * dh;
        const xAt = (yy: number) => ax0 + (yy - ay0 - k * dh) / (sign * slope);
        const pts: [number, number][] = [];
        for (const [xx, yy] of [[ax0, yAt(ax0)], [ax1, yAt(ax1)], [xAt(ay0), ay0], [xAt(ay1), ay1]] as [number, number][]) {
          if (xx >= ax0 - 1e-6 && xx <= ax1 + 1e-6 && yy >= ay0 - 1e-6 && yy <= ay1 + 1e-6) pts.push([Math.min(ax1, Math.max(ax0, xx)), Math.min(ay1, Math.max(ay0, yy))]);
        }
        if (pts.length < 2) continue;
        pts.sort((a, b) => a[0] - b[0]);
        const [a, b] = [pts[0], pts[pts.length - 1]];
        if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1 * IN) continue;
        brace(into, fr, a[0], a[1], b[0], b[1], thick * 0.8, z0, z1);
      }
    }
  };
  /** The pattern's bars over a sash's pane. */
  const bars = (kind: 'grid' | 'prairie' | 'fractional' | 'diamond' | 'none', ax0: number, ax1: number, ay0: number, ay1: number, into: Parts = p, dz = 0) => {
    const px0 = ax0 + sash, px1 = ax1 - sash, py0 = ay0 + sash, py1 = ay1 - sash;
    if (kind === 'grid') grid(px0, px1, py0, py1, into, dz);
    else if (kind === 'prairie') prairie(px0, px1, py0, py1, into, dz);
    else if (kind === 'fractional') fractional(px0, px1, py0, py1, into, dz);
    else if (kind === 'diamond') diamond(px0, px1, py0, py1, into, dz);
  };

  if (hungFamily) {
    // Two sashes, one over the other. Side by side panes on a wide window share
    // a mullion — one bar in EACH sash, so it stays in front of that sash's glass.
    // The pattern's bars go on each sash's own pane: both sashes (grid-hung,
    // prairie-hung), the upper only (traditional-hung), neither (hung).
    const upperBars = pattern === 'traditional-hung' || pattern === 'grid-hung' ? 'grid' : pattern === 'prairie-hung' ? 'prairie' : 'none';
    const lowerBars = pattern === 'grid-hung' ? 'grid' : pattern === 'prairie-hung' ? 'prairie' : 'none';
    const mullion = w >= 4 && pattern === 'hung' ? Math.min(1.25 * IN, f) : 0;
    const cx = (gx0 + gx1) / 2;
    if (opens && root) {
      // The upper sash and its pane stay put. The lower sash — its frame, its
      // pane and a lift handle on its bottom rail — runs in front of it (toward
      // the room) and slides up until its top meets the top of the frame: never
      // past the window, so never past the wall.
      p.box(GLASS, gx0, gx1, mid - sash / 2, gy1, gz - 0.3 * IN, gz + 0.3 * IN);
      sashAround(gx0, gx1, mid - sash / 2, gy1);
      if (mullion) bar(mullion, true, cx, mid - sash / 2 + sash, gy1 - sash);
      bars(upperBars, gx0, gx1, mid - sash / 2, gy1);
      const lower = new Parts();
      const dz = 1.1 * IN;
      lower.box(GLASS, gx0, gx1, gy0, mid + sash / 2, gz - 0.3 * IN + dz, gz + 0.3 * IN + dz);
      sashAround(gx0, gx1, gy0, mid + sash / 2, lower, dz);
      if (mullion) bar(mullion, true, cx, gy0 + sash, mid + sash / 2 - sash, lower, dz);
      bars(lowerBars, gx0, gx1, gy0, mid + sash / 2, lower, dz);
      const ry = gy0 + sash / 2;
      const front = sz1 + dz;
      lower.eased(hardware(palette), cx - 2 * IN, cx + 2 * IN, ry - 0.3 * IN, ry + 0.3 * IN, front, front + 0.7 * IN, 0.15 * IN, { grain: 'x' });
      const by = Math.max(0, gy1 - (mid + sash / 2));
      const handle = handleTarget(cx - 4 * IN, cx + 4 * IN, ry - 2.5 * IN, ry + 2.5 * IN, front - 0.5 * IN, front + 1.75 * IN);
      root.add(movingPart(o.key ?? `window@${o.x},${o.y}`, { kind: 'lift', by }, lower, handle));
    } else if (h > 2.5) {
      sashAround(gx0, gx1, gy0, mid + sash / 2);
      sashAround(gx0, gx1, mid - sash / 2, gy1);
      if (mullion) {
        bar(mullion, true, cx, gy0 + sash, mid + sash / 2 - sash);
        bar(mullion, true, cx, mid - sash / 2 + sash, gy1 - sash);
      }
      bars(lowerBars, gx0, gx1, gy0, mid + sash / 2);
      bars(upperBars, gx0, gx1, mid - sash / 2, gy1);
    } else {
      sashAround(gx0, gx1, gy0, gy1);
      if (mullion) bar(mullion, true, cx, gy0 + sash, gy1 - sash);
      bars(upperBars, gx0, gx1, gy0, gy1);
    }
  } else if (pattern !== 'picture') {
    // One sash, with the pattern's bars over its pane.
    sashAround(gx0, gx1, gy0, gy1);
    bars(pattern === 'grid' ? 'grid' : pattern === 'prairie' ? 'prairie' : pattern === 'fractional' ? 'fractional' : 'diamond', gx0, gx1, gy0, gy1);
  }
  // 'picture': the glass runs frame to frame.

  // Sill, standing into the room, with an apron under it.
  p.eased(sill(palette), x - 1 * IN, x1 + 1 * IN, y - 0.75 * IN, y + 0.5 * IN, Z_OUT * 0.2, Z_PROUD + 1.75 * IN, 0.3 * IN, { grain: 'x' });
}

// ============================================================================
// DOORS
// ============================================================================

/**
 * The glazed-and-panelled doors: stiles and rails of the body round glass in
 * lites along the top and panels below.
 *   craftsman      three lites across the top, two panels below
 *   craftsman-six  a 2 × 3 grid of lites, three panels below
 *   three-quarter  a 2 × 3 grid of glass down to a lock rail, one panel below
 *   prairie        the same with a 3 × 3 grid
 * The slab's face is the level of the sunk fields; rails, stiles and the
 * muntins stand proud of it; there is nothing behind the glass.
 */
function doorStyled(
  p: Parts, pattern: DoorPattern, palette: OpeningPalette,
  g: { sx0: number; sx1: number; sy0: number; sy1: number; sz0: number; sz1: number; proud: number; stile: number },
): void {
  const { sx0, sx1, sy0, sy1, sz0, sz1, proud, stile } = g;
  const sw = sx1 - sx0, sh = sy1 - sy0;
  const bd = body(palette), pn = panel(palette);
  const edge = 0.35 * IN;
  const top = Math.min(5 * IN, sh / 10);
  const bottom = Math.min(9 * IN, sh / 7.5);
  const craftsman = pattern === 'craftsman' || pattern === 'craftsman-six';
  // The glass: a band under the top rail (craftsman), or most of the door (three-quarter).
  const glassH = craftsman ? (pattern === 'craftsman' ? sh * 0.2 : sh * 0.28) : sh * 0.56;
  const lock = Math.min(5 * IN, sh / 10);
  const gy1 = sy1 - top, gy0 = gy1 - glassH;
  const gx0 = sx0 + stile, gx1 = sx1 - stile;
  // The sunk fields below the glass only.
  p.box(bd, sx0, sx1, sy0, gy0 - lock / 2 + 0.1 * IN, sz0, sz1, { shade: SHADE_FIELD });
  // Glass, and its lites.
  p.box(GLASS, gx0, gx1, gy0, gy1, sz1 - 0.5 * IN, sz1 - 0.2 * IN);
  const cols = pattern === 'craftsman' || pattern === 'craftsman-six' || pattern === 'prairie' ? 3 : 2;
  const rows = pattern === 'craftsman' ? 1 : 3;
  const bar = 0.8 * IN;
  for (let c = 1; c < cols; c++) {
    const mx = gx0 + ((gx1 - gx0) * c) / cols;
    p.bevel(bd, mx - bar / 2, mx + bar / 2, gy0, gy1, sz1 - 0.6 * IN, proud - 0.12 * IN, 0.15 * IN);
  }
  for (let r = 1; r < rows; r++) {
    const my = gy0 + ((gy1 - gy0) * r) / rows;
    p.bevel(bd, gx0, gx1, my - bar / 2, my + bar / 2, sz1 - 0.6 * IN, proud - 0.12 * IN, 0.15 * IN, { grain: 'x' });
  }
  // Stiles the door's full thickness (there is no slab behind the glass); the
  // top rail likewise; the rail under the glass and the bottom rail on the slab.
  p.bevel(bd, sx0, sx0 + stile, sy0, sy1, sz0, proud, edge);
  p.bevel(bd, sx1 - stile, sx1, sy0, sy1, sz0, proud, edge);
  p.box(bd, gx0, gx1, gy1, sy1, sz0, sz1, { grain: 'x' });
  p.bevel(bd, gx0, gx1, gy1, sy1, sz1, proud, edge, { grain: 'x' });
  p.bevel(bd, gx0, gx1, gy0 - lock, gy0, sz1, proud, edge, { grain: 'x' });
  p.bevel(bd, gx0, gx1, sy0, sy0 + bottom, sz1, proud, edge, { grain: 'x' });
  // The panels between the bottom rail and the rail under the glass.
  const py0 = sy0 + bottom, py1 = gy0 - lock;
  const panels = pattern === 'craftsman' ? 2 : pattern === 'craftsman-six' ? 3 : 1;
  const colW = (sw - (panels + 1) * stile) / panels;
  for (let c = 0; c < panels; c++) {
    const px = sx0 + stile + c * (colW + stile);
    if (c > 0) p.bevel(bd, px - stile, px, py0, py1, sz1, proud, edge); // the muntin before it
    const inset = 1.1 * IN;
    raisedPanel(p, pn, px + inset, px + colW - inset, py0 + inset, py1 - inset, sz1, proud - sz1 - 0.1 * IN);
  }
}

/** Where a door's lever is (for the handle you click), from addDoor. */
interface DoorHardware { latchX: number; leverY: number; proud: number }

function addDoor(frameParts: Parts, o: OpeningShape, leafParts?: Parts): DoorHardware {
  const p = leafParts ?? frameParts;
  const { pattern, palette } = openingStyle('door', o.style);
  const { x, y, width: w, height: h } = o;
  const j = Math.min(1 * IN, w / 10); // jamb thickness
  const x1 = x + w, y1 = y + h;
  const fr = frame(palette);

  // Jamb: both sides and the head. (It stays put; the slab and its hardware swing.)
  frameParts.eased(fr, x, x + j, y, y1, Z_OUT, Z_PROUD, 0.2 * IN);
  frameParts.eased(fr, x1 - j, x1, y, y1, Z_OUT, Z_PROUD, 0.2 * IN);
  frameParts.eased(fr, x + j, x1 - j, y1 - j, y1, Z_OUT, Z_PROUD, 0.2 * IN, { grain: 'x' });

  // Slab, hung within the jamb with a little clearance at the floor. Its face
  // is the level of the sunk fields; rails and panels stand proud of it.
  const sx0 = x + j + 0.1 * IN, sx1 = x1 - j - 0.1 * IN;
  const sy0 = y + 0.5 * IN, sy1 = y1 - j - 0.1 * IN;
  const sz0 = -1.75 * IN, sz1 = -0.45 * IN;
  const proud = 0;
  const sw = sx1 - sx0, sh = sy1 - sy0;
  const stile = Math.min(4.5 * IN, sw / 5);
  // The latch is on the side away from the hinges.
  const hingeRight = o.hinge === 'right';
  const latchX = hingeRight ? sx0 + stile / 2 : sx1 - stile / 2;
  const leverY = sy0 + Math.min(3, sh * 0.45);
  const bd = body(palette);
  const pn = panel(palette);

  if (pattern === 'flush') {
    p.eased(bd, sx0, sx1, sy0, sy1, sz0, proud, 0.12 * IN);
  } else if (pattern === 'slab-lites') {
    // A plain slab with five frosted lites stacked down its upper two thirds.
    p.eased(bd, sx0, sx1, sy0, sy1, sz0, proud, 0.12 * IN);
    const lw = sw * 0.55, lh = Math.min(5 * IN, sh / 16);
    const lx0 = sx0 + (sw - lw) / 2, lx1 = lx0 + lw;
    const from = sy0 + sh * 0.27, to = sy1 - sh * 0.1;
    const frost: Finish = { color: 0xe9eef1, opacity: 0.78, roughness: 0.3 };
    for (let i = 0; i < 5; i++) {
      const cy = from + ((to - from) * i) / 4;
      p.box(bd, lx0 - 0.3 * IN, lx1 + 0.3 * IN, cy - lh / 2 - 0.3 * IN, cy + lh / 2 + 0.3 * IN, sz0, proud + 0.1 * IN, { shade: 0.7 });
      p.box(frost, lx0, lx1, cy - lh / 2, cy + lh / 2, sz0 + 0.5 * IN, sz0 + 0.8 * IN);
    }
  } else if (pattern === 'craftsman' || pattern === 'craftsman-six' || pattern === 'three-quarter' || pattern === 'prairie') {
    doorStyled(p, pattern, palette, { sx0, sx1, sy0, sy1, sz0, sz1, proud, stile });
  } else {
    // The sunk fields: the slab's own face. A half-glass door has it only below
    // the lock rail — above, there is glass and nothing behind it.
    const halfGlass = pattern === 'half-glass';
    const glassFrom = sy0 + sh * 0.42; // the middle of the lock rail
    p.box(bd, sx0, sx1, sy0, halfGlass ? glassFrom + Math.min(5 * IN, sh / 10) / 2 : sy1, sz0, sz1, { shade: SHADE_FIELD });
    const edge = 0.35 * IN;
    const rails: number[][] = []; // [y0, y1] of each rail, bottom to top
    if (pattern === 'six-panel') {
      const rail = Math.min(5 * IN, sh / 10);
      const fieldH = sh - 4 * rail;
      const shares = [0.24, 0.42, 0.16]; // bottom, middle, top — shares of the field height
      let at = sy0;
      rails.push([at, at + rail]);
      at += rail;
      for (const share of shares) {
        const fh = (fieldH * share) / (shares[0] + shares[1] + shares[2]);
        // The raised panel in each of the two fields of this row.
        const colW = (sw - 3 * stile) / 2;
        for (let c = 0; c < 2; c++) {
          const px = sx0 + stile + c * (colW + stile);
          const inset = 1.1 * IN;
          raisedPanel(p, pn, px + inset, px + colW - inset, at + inset, at + fh - inset, sz1, proud - sz1 - 0.1 * IN);
        }
        at += fh;
        rails.push([at, at + rail]);
        at += rail;
      }
      // The muntin between the two columns.
      p.bevel(bd, sx0 + sw / 2 - stile / 2, sx0 + sw / 2 + stile / 2, sy0 + rail, sy1 - rail, sz1, proud, edge);
    } else {
      // Shaker / half-glass: one field below the lock rail, one above it.
      const top = Math.min(5 * IN, sh / 10);
      const bottom = Math.min(8 * IN, sh / 8);
      const lock = Math.min(5 * IN, sh / 10);
      const lockY = halfGlass ? glassFrom : sy0 + sh * 0.36;
      rails.push([sy0, sy0 + bottom], [lockY - lock / 2, lockY + lock / 2], [sy1 - top, sy1]);
      if (halfGlass) {
        p.box(GLASS, sx0 + stile, sx1 - stile, lockY + lock / 2, sy1 - top, sz1 - 0.5 * IN, sz1 - 0.2 * IN);
      }
    }
    // Stiles the full height; rails between them. (Round the glass they are the
    // door's full thickness: there is no slab there to stand on.)
    const stileBack = halfGlass ? sz0 : sz1;
    p.bevel(bd, sx0, sx0 + stile, sy0, sy1, stileBack, proud, edge);
    p.bevel(bd, sx1 - stile, sx1, sy0, sy1, stileBack, proud, edge);
    if (halfGlass) {
      const top = Math.min(5 * IN, sh / 10);
      p.box(bd, sx0 + stile, sx1 - stile, sy1 - top, sy1, sz0, sz1); // behind the top rail
    }
    for (const [r0, r1] of rails) p.bevel(bd, sx0 + stile, sx1 - stile, r0, r1, sz1, proud, edge, { grain: 'x' });
  }

  const hw = hardware(palette);
  if (pattern === 'flush' || pattern === 'slab-lites') {
    // A long bar pull, on two standoffs.
    const half = Math.min(9 * IN, sh / 6);
    p.eased(hw, latchX - 0.5 * IN, latchX + 0.5 * IN, leverY - half, leverY + half, proud + 0.9 * IN, proud + 1.6 * IN, 0.2 * IN);
    for (const at of [leverY - half * 0.75, leverY + half * 0.75]) {
      p.box(hw, latchX - 0.35 * IN, latchX + 0.35 * IN, at - 0.35 * IN, at + 0.35 * IN, proud, proud + 0.9 * IN);
    }
  } else {
    // Lever, 36" up, on the latch side: a rose, and the handle off it.
    p.bevel(hw, latchX - 1.1 * IN, latchX + 1.1 * IN, leverY - 1.1 * IN, leverY + 1.1 * IN, proud, proud + 0.5 * IN, 0.3 * IN);
    // (The lever points back toward the hinges.)
    const [l0, l1] = hingeRight ? [latchX - 0.4 * IN, latchX + 4.4 * IN] : [latchX - 4.4 * IN, latchX + 0.4 * IN];
    p.eased(hw, l0, l1, leverY - 0.4 * IN, leverY + 0.4 * IN, proud + 0.5 * IN, proud + 1.4 * IN, 0.2 * IN, { grain: 'x' });
  }
  return { latchX, leverY, proud };
}

/**
 * A door whose leaf swings open on its hinges. The leaf turns about the hinge
 * edge at the face it opens toward: this room's face (z = 0) when it opens in,
 * the far face of the wall (z = Z_OUT) when it opens out — which, for the two
 * copies of a door on a shared wall, is the same line, so the two halves turn
 * together as one door.
 */
function addSwingingDoor(frameParts: Parts, root: THREE.Group, o: OpeningShape): void {
  const leaf = new Parts();
  const hw = addDoor(frameParts, o, leaf);
  const j = Math.min(1 * IN, o.width / 10);
  const hingeRight = o.hinge === 'right';
  const hx = hingeRight ? o.x + o.width - j - 0.1 * IN : o.x + j + 0.1 * IN;
  const opensIn = o.swing !== 'out';
  const hz = opensIn ? 0 : Z_OUT;
  // Hinges left and opening in: the latch side (+x) comes toward the room.
  const sign: 1 | -1 = ((hingeRight ? 1 : -1) * (opensIn ? 1 : -1)) as 1 | -1;
  const handle = handleTarget(hw.latchX - 3 * IN, hw.latchX + 3 * IN, hw.leverY - 3 * IN, hw.leverY + 3 * IN, hw.proud - 0.5 * IN, hw.proud + 3 * IN);
  root.add(movingPart(o.key ?? `door@${o.x},${o.y}`, { kind: 'swing', hinge: [hx, hz], sign }, leaf, handle));
}

// ============================================================================
// GARAGE DOORS
// ============================================================================

const GARAGE_JAMB = 1.5 * IN;

/** How many sections a garage door of this height folds in: about 21" each. */
const garageSections = (doorHeight: number) => Math.max(3, Math.round(doorHeight / 1.75));

/**
 * A garage door. The frame stays put; each section is a moving part of its own
 * (drawn as the whole door, cut to that section's band), and an opener button
 * box on the wall beside the door is what you click to send it up or down.
 */
/** The ceiling's height over a point: `x` along the wall, `z` into the room (canonical wall coordinates, feet). */
export type CeilingAt = (x: number, z: number) => number;

function addGarageDoor(p: Parts, root: THREE.Group, o: OpeningShape, wallLength: number, ceilingAt: CeilingAt | null): void {
  const { palette } = openingStyle('garageDoor', o.style);
  const { x, y, width: w, height: h } = o;
  const j = GARAGE_JAMB;
  const x1 = x + w, y1 = y + h;
  const fr = frame(palette);

  // Frame: both sides and the head.
  p.eased(fr, x, x + j, y, y1, Z_OUT, Z_PROUD, 0.25 * IN);
  p.eased(fr, x1 - j, x1, y, y1, Z_OUT, Z_PROUD, 0.25 * IN);
  p.eased(fr, x + j, x1 - j, y1 - j, y1, Z_OUT, Z_PROUD, 0.25 * IN, { grain: 'x' });

  const key = o.key ?? `garage@${o.x},${o.y}`;
  const dy0 = y, dy1 = y1 - j;
  const dh = dy1 - dy0;
  const sections = garageSections(dh);
  const secH = dh / sections;
  // The track curves over above the opening, as tightly as the headroom needs
  // (15" where there is room for the spring and opener above it as well) — the
  // headroom being the lowest the ceiling comes over the door, along the wall.
  const headroom = ceilingAt ? Math.min(...[0, 0.25, 0.5, 0.75, 1].map((f) => ceilingAt(x + j + (w - 2 * j) * f, 0.5))) - dy1 : Infinity;
  const radius = Math.max(0.3, Math.min(1.25, headroom - 0.7));
  const faceZ = -1 * IN; // the door's room face, closed
  // Moving, it rides this far out from there: its back (1.4" behind its face)
  // then clears the frame over the opening (which stands to Z_PROUD) with room to spare.
  const clear = Z_PROUD + 0.3 * IN - (faceZ - 1.4 * IN);
  for (let s = 0; s < sections; s++) {
    const part = new Parts();
    part.clipY = [dy0 + s * secH, dy0 + (s + 1) * secH];
    garageDoorBody(part, o);
    root.add(movingPart(key, { kind: 'roll', y0: dy0 + s * secH, y1: dy0 + (s + 1) * secH, top: dy1, z: faceZ, r: radius, travel: dh, clear }, part));
  }
  if (ceilingAt) addGarageHardware(root, o, { key, dy0, dy1, dh, radius, faceZ: faceZ + clear, ceilingAt, sections });

  // The opener's wall button: a small box beside the door, at about 4' 6", on
  // whichever side there is wall for it.
  const bw = 2.75 * IN, bh = 4.5 * IN;
  const right = x1 + 8 * IN + bw <= wallLength - 2 * IN;
  const bx0 = right ? x1 + 8 * IN : x - 8 * IN - bw;
  const by0 = y + 4.5;
  const opener = new Parts();
  opener.eased({ color: 0xefece4, roughness: 0.5 }, bx0, bx0 + bw, by0, by0 + bh, 0.4 * IN, 1.3 * IN, 0.15 * IN);
  opener.eased({ color: 0x3a3d42, roughness: 0.4 }, bx0 + 0.6 * IN, bx0 + bw - 0.6 * IN, by0 + 1.6 * IN, by0 + bh - 0.9 * IN, 1.3 * IN, 1.6 * IN, 0.15 * IN);
  const button = movingPart(key, { kind: 'fixed' }, opener, handleTarget(bx0 - 1 * IN, bx0 + bw + 1 * IN, by0 - 1 * IN, by0 + bh + 1 * IN, 0, 2.25 * IN));
  button.name = 'garage-opener';
  root.add(button);
}

/**
 * Where everything round a garage door is, in the wall's canonical
 * coordinates (feet; x along the wall, y up, z out into the room): the very
 * numbers `addGarageHardware` draws from, kept on its group
 * (`userData.garageRig`) for the photoreal renderer, which builds the hardware
 * in full from them (scripts/blender/garage_hardware.py) — the tracks' real
 * profile round the real curve, the spring, the opener — where the browser
 * draws its simple stand-ins. Recording them changes nothing that is drawn.
 * The ceiling heights are those the browser cut each part to, plus a grid
 * over the hardware's footprint for anything the renderer adds.
 */
export interface GarageRig {
  version: 1;
  /** The door's openerKey: its sections (moving parts with the same key) carry their rollers in the render. */
  key: string;
  door: { x0: number; x1: number; y0: number; y1: number; sections: number };
  /** The line the door's room face rides on (z, when closed), and the curve's radius and centre. */
  track: {
    faceZ: number; r: number; centreY: number; centreZ: number;
    /** The horizontal runs' middle (y) and far end (z). */
    trackY: number; runZ: number;
    /** Each track's extent across the wall (x), left then right; its half depth (the browser's channel). */
    sides: [number, number][]; halfDepth: number;
    /** The face of the door's frame and the wall round it (z): where brackets bolt on. */
    wallZ: number;
  };
  /** The hangers from the horizontal tracks to the ceiling, as drawn: x extent, z centre, from (y) to the ceiling at z0/z1. */
  hangers: { x0: number; x1: number; z: number; z0: number; z1: number; from: number; ceiling0: number; ceiling1: number; brace: number }[];
  /** The torsion spring over the opening, if there is headroom for it. */
  spring: { y: number; z: number; x0: number; x1: number; drums: [number, number][]; coil: [number, number]; pad: number } | null;
  /** The opener, if there is headroom for it: its rail, trolley, motor box and straps. */
  opener: {
    cx: number; railY: number; railZ0: number; railZ1: number; trolleyZ: [number, number];
    motor: { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number };
    straps: { x: number; z0: number; z1: number; top: number; ceiling0: number; ceiling1: number }[];
  } | null;
  /** The ceiling over the hardware: heights at xs × zs (row per x). */
  ceiling: { xs: number[]; zs: number[]; heights: number[][] };
}

/**
 * What hangs round a garage door, drawn to fill out the picture (a look only:
 * nothing here is counted): the tracks — up each side of the opening, round the
 * curve and back under the ceiling, in bright aluminium — with hangers from
 * their far ends (and middles) up to the ceiling, however high it is; the
 * torsion spring on its shaft over the opening, with its centre spring pad and
 * a cable drum at each end; and an automatic opener, its rail running from the
 * spring pad back to the motor hung from the ceiling.
 * Anything there is no headroom for is left out.
 */
function addGarageHardware(root: THREE.Group, o: OpeningShape, g: { key: string; dy0: number; dy1: number; dh: number; radius: number; faceZ: number; ceilingAt: CeilingAt; sections: number }): void {
  const { dy0, dy1, dh, radius: r, faceZ, ceilingAt } = g;
  const p = new Parts();
  // (No environment to reflect, so "shiny" is a bright, smooth, lightly metallic finish.)
  const metal: Finish = { color: 0xeef1f4, roughness: 0.22, metalness: 0.25 };
  const steel: Finish = { color: 0xc3c8cd, roughness: 0.32, metalness: 0.25 };
  const dark: Finish = { color: 0x2c2e31, roughness: 0.45, metalness: 0.4 };
  const motor: Finish = { color: 0x3d4045, roughness: 0.55 };
  const j = GARAGE_JAMB;
  const dx0 = o.x + j, dx1 = o.x + o.width - j;
  const cx = (dx0 + dx1) / 2;

  // ---- Tracks: a channel each side, just over the door's edge, in front of it.
  // (`faceZ` here is where the door's face rides once it is moving: the tracks
  // follow that line, just outside the door's edges, so the two never meet.)
  const off = 0;                        // from the door's face to the middle of the track
  const tr = r - off;                   // the track's own curve (the same centre)
  const centreY = dy1, centreZ = faceZ + r;
  const runZ = faceZ + r + dh + 1.0;    // where the horizontal track ends
  const trackY = dy1 + r - off;         // the horizontal track's middle
  const halfDepth = 0.75 * IN;          // the channel, across the track
  const sides: [number, number][] = [[dx0 - 1.6 * IN, dx0 - 0.1 * IN], [dx1 + 0.1 * IN, dx1 + 1.6 * IN]];
  const rig: GarageRig = {
    version: 1,
    key: g.key,
    door: { x0: dx0, x1: dx1, y0: dy0, y1: dy1, sections: g.sections },
    track: { faceZ, r, centreY, centreZ, trackY, runZ, sides, halfDepth, wallZ: Z_PROUD },
    hangers: [],
    spring: null,
    opener: null,
    ceiling: { xs: [], zs: [], heights: [] },
  };
  for (const [a, b] of sides) {
    // Up the side of the opening.
    p.box(metal, a, b, dy0 + 0.1 * IN, dy1, faceZ + off - halfDepth, faceZ + off + halfDepth);
    // Round the curve, in short lengths.
    const steps = 8;
    for (let i = 0; i < steps; i++) {
      const t0 = (i / steps) * (Math.PI / 2), t1 = ((i + 1) / steps) * (Math.PI / 2);
      const pt = (t: number, rad: number): [number, number] => [centreY + rad * Math.sin(t), centreZ - rad * Math.cos(t)];
      p.prism(metal, a, b, [pt(t0, tr - halfDepth), pt(t1, tr - halfDepth), pt(t1, tr + halfDepth), pt(t0, tr + halfDepth)]);
    }
    // Back into the room.
    p.box(metal, a, b, trackY - halfDepth, trackY + halfDepth, centreZ, runZ);
    // Hangers: at the far end, and half way, each up to the ceiling where IT
    // is (a sloped ceiling is a different height over each one), with a brace.
    const hangFrom = trackY + halfDepth;
    // (On the outer part of the track, clear of the door's edge as it rides past.)
    const [ha, hb] = a < cx ? [a, b - 0.4 * IN] : [a + 0.4 * IN, b];
    for (const hz of [runZ - 0.15, (centreZ + runZ) / 2]) {
      const hx = (ha + hb) / 2;
      const ceiling = ceilingAt(hx, hz);
      if (ceiling - hangFrom <= 0.15) continue;
      // The hanger's top is cut to the ceiling's slope, so it sits flat on it.
      const z0 = hz - 0.6 * IN, z1 = hz + 0.6 * IN;
      p.prism(steel, ha, hb, [[hangFrom, z0], [hangFrom, z1], [ceilingAt(hx, z1), z1], [ceilingAt(hx, z0), z0]]);
      const brace = Math.min(0.9, (ceiling - hangFrom) * 0.8);
      rig.hangers.push({ x0: ha, x1: hb, z: hz, z0, z1, from: hangFrom, ceiling0: ceilingAt(hx, z0), ceiling1: ceilingAt(hx, z1), brace: brace > 0.25 ? brace : 0 });
      if (brace > 0.25) {
        const b0 = hz - brace, b1 = hz - brace + 1.2 * IN;
        p.prism(steel, ha, hb, [[ceilingAt(hx, b0), b0], [ceilingAt(hx, b1), b1], [ceiling - brace, hz + 0.5 * IN], [ceiling - brace, hz - 0.5 * IN]]);
      }
    }
  }

  // ---- Spring, pad and drums: over the opening, above the curve.
  const springY = dy1 + r + 0.35;
  const springZ = 4 * IN;
  if (springY + 3 * IN < ceilingAt(cx, springZ)) {
    const shaftX0 = sides[0][0] - 1.5 * IN, shaftX1 = sides[1][1] + 1.5 * IN;
    p.cylinderX(steel, shaftX0, shaftX1, springY, springZ, 0.5 * IN, 10);
    // Cable drums and their end brackets.
    for (const [a, b] of [[shaftX0, shaftX0 + 1.75 * IN], [shaftX1 - 1.75 * IN, shaftX1]]) {
      p.cylinderX(dark, a, b, springY, springZ, 2.2 * IN, 16);
      p.box(steel, a - 0.4 * IN, a, springY - 3.5 * IN, springY + 3.5 * IN, 0, springZ + 2.5 * IN);
    }
    // The spring: a coil of dark wire on the shaft, to one side of the pad.
    const coilLen = Math.min(2.5, Math.max(0.5, (dx1 - dx0) / 2 - 0.6));
    const coilX1 = cx - 3 * IN, coilX0 = coilX1 - coilLen;
    const turns = Math.round(coilLen / (0.32 * IN));
    for (let k = 0; k < turns; k++) {
      const kx = coilX0 + (k * coilLen) / turns;
      p.cylinderX(dark, kx, kx + 0.22 * IN, springY, springZ, 1.1 * IN, 10);
    }
    // The spring pad: a bracket on the wall at the middle.
    p.box(dark, cx - 3 * IN, cx + 3 * IN, springY - 4 * IN, springY + 4 * IN, 0, 1 * IN);
    p.box(dark, cx - 1.5 * IN, cx + 1.5 * IN, springY - 2 * IN, springY + 2 * IN, 1 * IN, springZ + 1.5 * IN);
    rig.spring = {
      y: springY, z: springZ, x0: shaftX0, x1: shaftX1,
      drums: [[shaftX0, shaftX0 + 1.75 * IN], [shaftX1 - 1.75 * IN, shaftX1]], coil: [coilX0, coilX1], pad: cx,
    };
  }

  // ---- Opener: rail from over the pad back to the motor, hung from the ceiling.
  // The rail runs just above the door as it lies open on the tracks.
  const railY = dy1 + r + 0.3;
  const motorZ0 = runZ - 0.4, motorZ1 = motorZ0 + 1.5;
  const ceiling = ceilingAt(cx, motorZ0 + 0.72); // over the motor's straps
  if (railY + 0.15 < ceiling) {
    p.box(steel, cx - 0.7 * IN, cx + 0.7 * IN, railY - 0.6 * IN, railY + 0.6 * IN, springZ + 1.5 * IN, motorZ0);
    // The header bracket the rail starts from.
    p.box(dark, cx - 2 * IN, cx + 2 * IN, railY - 2 * IN, railY + 1 * IN, springZ + 1 * IN, springZ + 3 * IN);
    // A trolley on the rail (where the arm to the door would hang).
    p.box(dark, cx - 1.6 * IN, cx + 1.6 * IN, railY - 2 * IN, railY - 0.6 * IN, centreZ + 0.4, centreZ + 0.9);
    // The motor: a box hung a little under the ceiling (but always round the
    // rail's end), a little lighter underneath where the light lens is.
    let top = ceiling - 0.2;
    let bottom = top - 0.6;
    if (bottom > railY - 0.15) { bottom = railY - 0.15; top = Math.min(ceiling - 0.05, bottom + 0.6); }
    p.eased(motor, cx - 0.65, cx + 0.65, bottom, top, motorZ0, motorZ1, 0.6 * IN);
    p.box({ color: 0xe9e6dc, roughness: 0.4 }, cx - 0.45, cx + 0.45, bottom - 0.25 * IN, bottom, motorZ0 + 0.3, motorZ1 - 0.3);
    // Hung by two straps, each cut to the ceiling over it.
    const straps: NonNullable<GarageRig['opener']>['straps'] = [];
    for (const sx of [cx - 0.55, cx + 0.55]) {
      const z0 = motorZ0 + 0.7, z1 = motorZ0 + 0.75;
      p.prism(steel, sx - 0.5 * IN, sx + 0.5 * IN, [[top, z0], [top, z1], [ceilingAt(sx, z1), z1], [ceilingAt(sx, z0), z0]]);
      straps.push({ x: sx, z0, z1, top, ceiling0: ceilingAt(sx, z0), ceiling1: ceilingAt(sx, z1) });
    }
    rig.opener = {
      cx, railY, railZ0: springZ + 1.5 * IN, railZ1: motorZ0, trolleyZ: [centreZ + 0.4, centreZ + 0.9],
      motor: { x0: cx - 0.65, x1: cx + 0.65, y0: bottom, y1: top, z0: motorZ0, z1: motorZ1 },
      straps,
    };
  }

  // The ceiling over it all, on a grid (a quarter foot along, the tracks and the middle across).
  const xs = [sides[0][0] - 0.25, sides[0][0], cx, sides[1][1], sides[1][1] + 0.25];
  const zs: number[] = [];
  for (let z = 0; z <= runZ + 2 + 1e-9; z += 0.25) zs.push(z);
  rig.ceiling = { xs, zs, heights: xs.map((cxx) => zs.map((cz) => ceilingAt(cxx, cz))) };

  const group = new THREE.Group();
  group.name = 'garage-hardware';
  group.userData.garageRig = rig;
  p.into(group);
  // Out of the ambient-occlusion pass: thin parts against the ceiling smudge it.
  group.traverse((c) => { c.userData.skipAO = true; });
  root.add(group);
}

/** The door itself (all of it — the caller cuts it to one section's band). */
function garageDoorBody(p: Parts, o: OpeningShape): void {
  const { pattern, palette } = openingStyle('garageDoor', o.style);
  const { x, y } = o;
  const j = GARAGE_JAMB;
  const x1 = x + o.width, y1 = y + o.height;

  // The door itself, set back in the frame.
  const dx0 = x + j, dx1 = x1 - j, dy0 = y, dy1 = y1 - j;
  const dz0 = -2.4 * IN, dz1 = -1 * IN;
  const dw = dx1 - dx0, dh = dy1 - dy0;
  const bd = body(palette);
  const pn = panel(palette);
  const hw = hardware(palette);

  // Horizontal sections about 21" tall — how every sectional door folds.
  const sections = garageSections(dh);
  const secH = dh / sections;
  const cx = (dx0 + dx1) / 2;

  if (pattern === 'raised') {
    // Each section is its own slab with eased edges (so the joints between
    // sections are real grooves), carrying a row of raised panels.
    const panels = Math.max(2, Math.round(dw / 4)) * 2; // about 2' wide each
    const gap = 3.2 * IN;
    const panelW = (dw - gap * (panels + 1)) / panels;
    for (let s = 0; s < sections; s++) {
      const sy = dy0 + s * secH;
      p.box(line(palette), dx0, dx1, sy, sy + secH, dz0, dz1 - 0.35 * IN, { shade: 0.8 });
      p.bevel(bd, dx0, dx1, sy + 0.12 * IN, sy + secH - 0.12 * IN, dz1 - 0.35 * IN, dz1, 0.3 * IN, { grain: 'x' });
      for (let c = 0; c < panels; c++) {
        const px = dx0 + gap + c * (panelW + gap);
        raisedPanel(p, pn, px, px + panelW, sy + gap, sy + secH - gap, dz1, 0.5 * IN, { grain: 'x' });
      }
    }
    // Lift handle, low on the centre line.
    p.eased(hw, cx - 3.2 * IN, cx + 3.2 * IN, dy0 + 0.6 * secH - 0.45 * IN, dy0 + 0.6 * secH + 0.45 * IN, dz1 + 0.5 * IN, dz1 + 1.5 * IN, 0.25 * IN, { grain: 'x' });
  } else if (pattern === 'carriage') {
    // Made to look like a pair (or two pairs) of swinging doors: each leaf is
    // planked, framed with boards, glazed across the top and strap-hinged.
    const leaves = dw >= 12 ? 4 : 2;
    const leafW = dw / leaves;
    const board = Math.min(5 * IN, leafW / 6);
    const proud = dz1 + 0.55 * IN;
    const lockY = dy0 + dh * 0.72; // the rail under the glass
    // Backing behind the planks — not behind the glass.
    p.box(bd, dx0, dx1, dy0, lockY, dz0, dz1 - 0.3 * IN, { shade: 0.6 });
    for (let i = 0; i < leaves; i++) {
      const lx0 = dx0 + i * leafW, lx1 = lx0 + leafW;
      // Planks, each a board with eased edges, so the joints between them are V-grooves.
      const planks = Math.max(2, Math.round((leafW - 2 * board) / 0.5));
      const plankW = (leafW - 2 * board) / planks;
      for (let k = 0; k < planks; k++) {
        const gx = lx0 + board + k * plankW;
        p.bevel(bd, gx, gx + plankW, dy0 + board, lockY - board / 2, dz1 - 0.3 * IN, dz1, 0.22 * IN, { shade: 0.95 });
      }
      // Frame boards, over the planks.
      const edge = 0.28 * IN;
      p.bevel(pn, lx0 + 0.1 * IN, lx0 + board, dy0, dy1, dz1, proud, edge);
      p.bevel(pn, lx1 - board, lx1 - 0.1 * IN, dy0, dy1, dz1, proud, edge);
      p.bevel(pn, lx0 + board, lx1 - board, dy0, dy0 + board, dz1, proud, edge, { grain: 'x' });
      p.bevel(pn, lx0 + board, lx1 - board, dy1 - board, dy1, dz1, proud, edge, { grain: 'x' });
      // Above the rail the boards are all there is: give them the door's thickness.
      p.box(pn, lx0 + 0.1 * IN, lx0 + board, lockY, dy1, dz0, dz1);
      p.box(pn, lx1 - board, lx1 - 0.1 * IN, lockY, dy1, dz0, dz1);
      p.box(pn, lx0 + board, lx1 - board, dy1 - board, dy1, dz0, dz1, { grain: 'x' });
      p.bevel(pn, lx0 + board, lx1 - board, lockY - board / 2, lockY + board / 2, dz1, proud, edge, { grain: 'x' });
      // Glass across the top, in panes.
      const gx0 = lx0 + board, gx1 = lx1 - board, gy0 = lockY + board / 2, gy1 = dy1 - board;
      p.box(GLASS, gx0, gx1, gy0, gy1, dz1 - 0.6 * IN, dz1 - 0.3 * IN);
      const panes = Math.max(2, Math.round((gx1 - gx0) / 1.1));
      for (let k = 1; k < panes; k++) {
        const mx = gx0 + ((gx1 - gx0) * k) / panes;
        p.bevel(pn, mx - 0.6 * IN, mx + 0.6 * IN, gy0, gy1, dz1 - 0.8 * IN, proud - 0.15 * IN, 0.2 * IN);
      }
      // Strap hinges on the outer edge of each pair; a pull where the pair meets.
      const hingeOnLeft = i % 2 === 0;
      const strap = Math.min(11 * IN, leafW * 0.45);
      for (const at of [dy0 + dh * 0.16, dy0 + dh * 0.6]) {
        const hx0 = hingeOnLeft ? lx0 + 0.1 * IN : lx1 - 0.1 * IN - strap;
        p.bevel(hw, hx0, hx0 + strap, at - 0.75 * IN, at + 0.75 * IN, proud, proud + 0.25 * IN, 0.12 * IN, { grain: 'x' });
      }
      const pullX = hingeOnLeft ? lx1 - board / 2 : lx0 + board / 2;
      const pullY = dy0 + dh * 0.36;
      p.eased(hw, pullX - 0.5 * IN, pullX + 0.5 * IN, pullY, pullY + 9 * IN, proud + 0.6 * IN, proud + 1.3 * IN, 0.2 * IN);
      for (const at of [pullY + 1 * IN, pullY + 8 * IN]) {
        p.box(hw, pullX - 0.3 * IN, pullX + 0.3 * IN, at - 0.3 * IN, at + 0.3 * IN, proud, proud + 0.6 * IN);
      }
    }
  } else if (pattern === 'planks') {
    // Modern flush: horizontal planks the whole width of the door, V-grooved,
    // with a stack of slim windows down one side (one a section, all but the
    // lowest) in slim dark frames.
    // A whole number of plank rows to each section (about 6" each), so a
    // window — exactly two rows tall, centred in its section — never cuts a
    // plank: in its rows the planks simply stop either side of it, their ends
    // under the frame. One window a section, so they are evenly spaced.
    const perSection = Math.max(2, Math.round(secH / (6 * IN)));
    const ph = secH / perSection;
    const wRows = perSection >= 3 ? 2 : 1;
    const firstRow = Math.floor((perSection - wRows) / 2);
    const ww = Math.min(2.4, dw * 0.3);
    const wx1 = dx1 - Math.max(0.8, dw * 0.12), wx0 = wx1 - ww;
    const windows: { y0: number; y1: number }[] = [];
    for (let s = 0; s < sections; s++) {
      const y0 = dy0 + s * secH + firstRow * ph;
      windows.push({ y0, y1: y0 + wRows * ph });
    }
    const inWindow = (y0: number, y1: number) => windows.some((w) => w.y0 < y1 - 1e-6 && w.y1 > y0 + 1e-6);
    // Backing, left out behind the glass.
    for (const [a, b] of [[dx0, wx0], [wx1, dx1]]) p.box(bd, a, b, dy0, dy1, dz0, dz1 - 0.3 * IN, { shade: 0.6 });
    for (let s = 0; s < sections; s++) {
      for (let r = 0; r < perSection; r++) {
        const ry0 = dy0 + s * secH + r * ph, ry1 = ry0 + ph;
        if (!inWindow(ry0, ry1)) p.box(bd, wx0, wx1, ry0, ry1, dz0, dz1 - 0.3 * IN, { shade: 0.6 });
        // The plank: the full width, or either side of the window.
        const spans = inWindow(ry0, ry1) ? [[dx0, wx0 + 0.1 * IN], [wx1 - 0.1 * IN, dx1]] : [[dx0, dx1]];
        for (const [a, b] of spans) p.bevel(bd, a, b, ry0, ry1, dz1 - 0.3 * IN, dz1, 0.2 * IN, { grain: 'x', shade: 0.97 });
      }
    }
    // The joints between sections, as shadow lines.
    for (let s = 1; s < sections; s++) {
      const sy = dy0 + s * secH;
      p.box(line(palette), dx0, dx1, sy - 0.08 * IN, sy + 0.08 * IN, dz1 - 0.35 * IN, dz1 + 0.02 * IN, { shade: 0.5 });
    }
    // The windows: a dark frame standing a little proud of the planks and
    // lapping over their ends, glass set in it.
    const dark: Finish = { color: 0x1d1d1f, roughness: 0.5 };
    const f = 1.3 * IN, lap = 0.4 * IN;
    for (const w of windows) {
      p.eased(dark, wx0 - lap, wx0 + f, w.y0 - lap, w.y1 + lap, dz1 - 0.4 * IN, dz1 + 0.2 * IN, 0.1 * IN);
      p.eased(dark, wx1 - f, wx1 + lap, w.y0 - lap, w.y1 + lap, dz1 - 0.4 * IN, dz1 + 0.2 * IN, 0.1 * IN);
      p.eased(dark, wx0 + f, wx1 - f, w.y0 - lap, w.y0 + f, dz1 - 0.4 * IN, dz1 + 0.2 * IN, 0.1 * IN, { grain: 'x' });
      p.eased(dark, wx0 + f, wx1 - f, w.y1 - f, w.y1 + lap, dz1 - 0.4 * IN, dz1 + 0.2 * IN, 0.1 * IN, { grain: 'x' });
      p.box(GLASS, wx0 + f, wx1 - f, w.y0 + f, w.y1 - f, dz1 - 0.7 * IN, dz1 - 0.4 * IN);
    }
    // A slim lift handle, low and to one side.
    p.eased(hw, cx - 3.2 * IN, cx + 3.2 * IN, dy0 + 0.6 * secH - 0.4 * IN, dy0 + 0.6 * secH + 0.4 * IN, dz1 + 0.4 * IN, dz1 + 1.2 * IN, 0.2 * IN, { grain: 'x' });
  } else if (pattern === 'x-brace' || pattern === 'barn') {
    // Carriage doors with cross braces: each leaf a frame of boards round a
    // field — flat (x-brace) or vertically planked (barn) — crossed by two
    // diagonal boards, with a row of divided-lite windows along the top.
    carriageBraced(p, o, palette, pattern === 'barn');
  } else {
    // Full-view: a grid of glass in slim metal bars.
    const cols = Math.max(2, Math.round(dw / 2.25));
    const barW = 2.25 * IN;
    const proud = dz1 + 0.45 * IN;
    p.box(GLASS, dx0, dx1, dy0, dy1, dz1 - 0.8 * IN, dz1 - 0.5 * IN);
    // The bars are the door: its full thickness, with a bevelled face.
    for (let s = 0; s <= sections; s++) {
      const sy = Math.min(dy1 - barW / 2, Math.max(dy0 + barW / 2, dy0 + s * secH));
      p.box(line(palette), dx0, dx1, sy - barW / 2, sy + barW / 2, dz0, dz1 - 0.25 * IN, { grain: 'x' });
      p.bevel(line(palette), dx0, dx1, sy - barW / 2, sy + barW / 2, dz1 - 0.25 * IN, proud, 0.3 * IN, { grain: 'x' });
    }
    for (let c = 0; c <= cols; c++) {
      const bx = Math.min(dx1 - barW / 2, Math.max(dx0 + barW / 2, dx0 + (dw * c) / cols));
      p.box(line(palette), bx - barW / 2, bx + barW / 2, dy0, dy1, dz0, dz1 - 0.25 * IN);
      p.bevel(line(palette), bx - barW / 2, bx + barW / 2, dy0, dy1, dz1 - 0.25 * IN, proud, 0.3 * IN);
    }
  }
}

/** A board `wd` wide from (ax, ay) to (bx, by), lying on a face from z0 to z1 — a cross brace. */
function brace(p: Parts, finish: Finish, ax: number, ay: number, bx: number, by: number, wd: number, z0: number, z1: number, options: PartOptions = {}): void {
  const dx = bx - ax, dy = by - ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-4) return;
  const nx = (-dy / len) * (wd / 2), ny = (dx / len) * (wd / 2);
  p.slab(finish, [[ax + nx, ay + ny], [bx + nx, by + ny], [bx - nx, by - ny], [ax - nx, ay - ny]], z0, z1, options);
}

/**
 * Carriage doors with cross braces (the whole door; the caller cuts it to a
 * section's band). Two leaves, or four on a wide door. Each leaf: stiles and
 * rails of board round a field — flat, or vertical planks (`planked`) — with
 * two diagonal boards crossing it corner to corner under a rail, and over the
 * rail a row of divided-lite windows. Strap hinges on the outer edges, a pull
 * where a pair meets.
 */
function carriageBraced(p: Parts, o: OpeningShape, palette: OpeningPalette, planked: boolean): void {
  const { x, y } = o;
  const j = GARAGE_JAMB;
  const dx0 = x + j, dx1 = x + o.width - j, dy0 = y, dy1 = y + o.height - j;
  const dz0 = -2.4 * IN, dz1 = -1 * IN;
  const dw = dx1 - dx0, dh = dy1 - dy0;
  const bd = body(palette), pn = panel(palette), hw = hardware(palette);
  const leaves = dw >= 12 ? 4 : 2;
  const leafW = dw / leaves;
  const board = Math.min(5 * IN, leafW / 7);
  const proud = dz1 + 0.55 * IN;
  const edge = 0.28 * IN;
  // The window band along the top: about a fifth of the door, never more than 16".
  const bandH = Math.min(16 * IN, dh * 0.22);
  const railY = dy1 - bandH; // the rail under the glass
  // Backing behind the field (not behind the glass).
  p.box(bd, dx0, dx1, dy0, railY, dz0, dz1 - 0.3 * IN, { shade: 0.6 });
  for (let i = 0; i < leaves; i++) {
    const lx0 = dx0 + i * leafW, lx1 = lx0 + leafW;
    const fx0 = lx0 + board, fx1 = lx1 - board; // the field, between the stiles
    const fy0 = dy0 + board, fy1 = railY - board / 2;
    if (planked) {
      // Vertical planks with V-grooves between them.
      const planks = Math.max(2, Math.round((fx1 - fx0) / 0.45));
      const pw = (fx1 - fx0) / planks;
      for (let k = 0; k < planks; k++) p.bevel(bd, fx0 + k * pw, fx0 + (k + 1) * pw, fy0, fy1, dz1 - 0.3 * IN, dz1, 0.2 * IN, { shade: 0.95 });
    } else {
      p.box(bd, fx0, fx1, fy0, fy1, dz1 - 0.3 * IN, dz1, { shade: 0.97 });
    }
    // Stiles, bottom rail, the rail under the glass, top rail.
    p.bevel(pn, lx0 + 0.1 * IN, lx0 + board, dy0, dy1, dz1, proud, edge);
    p.bevel(pn, lx1 - board, lx1 - 0.1 * IN, dy0, dy1, dz1, proud, edge);
    p.bevel(pn, fx0, fx1, dy0, dy0 + board, dz1, proud, edge, { grain: 'x' });
    p.bevel(pn, fx0, fx1, railY - board / 2, railY + board / 2, dz1, proud, edge, { grain: 'x' });
    p.bevel(pn, fx0, fx1, dy1 - board, dy1, dz1, proud, edge, { grain: 'x' });
    // Above the rail the boards are all there is: give them the door's thickness.
    p.box(pn, lx0 + 0.1 * IN, lx0 + board, railY, dy1, dz0, dz1);
    p.box(pn, lx1 - board, lx1 - 0.1 * IN, railY, dy1, dz0, dz1);
    p.box(pn, fx0, fx1, dy1 - board, dy1, dz0, dz1, { grain: 'x' });
    // The cross: corner to corner of the field, one board over the other.
    const bw = board * 0.9;
    brace(p, pn, fx0, fy0, fx1, fy1, bw, dz1, proud - 0.08 * IN, { shade: 0.98 });
    brace(p, pn, fx0, fy1, fx1, fy0, bw, dz1, proud, { shade: 0.98 });
    // Glass across the top, in lites.
    const gx0 = fx0, gx1 = fx1, gy0 = railY + board / 2, gy1 = dy1 - board;
    p.box(GLASS, gx0, gx1, gy0, gy1, dz1 - 0.6 * IN, dz1 - 0.3 * IN);
    const cols = Math.max(2, Math.round((gx1 - gx0) / 0.55));
    const rows = gy1 - gy0 > 0.95 ? 2 : 1;
    const bar = 0.5 * IN;
    for (let k = 1; k < cols; k++) {
      const mx = gx0 + ((gx1 - gx0) * k) / cols;
      p.bevel(pn, mx - bar / 2, mx + bar / 2, gy0, gy1, dz1 - 0.8 * IN, proud - 0.15 * IN, 0.12 * IN);
    }
    for (let k = 1; k < rows; k++) {
      const my = gy0 + ((gy1 - gy0) * k) / rows;
      p.bevel(pn, gx0, gx1, my - bar / 2, my + bar / 2, dz1 - 0.8 * IN, proud - 0.15 * IN, 0.12 * IN, { grain: 'x' });
    }
    // Strap hinges on the outer edge of each pair; a pull where the pair meets.
    const hingeOnLeft = i % 2 === 0;
    const strap = Math.min(11 * IN, leafW * 0.45);
    for (const at of [dy0 + dh * 0.16, dy0 + dh * 0.6]) {
      const hx0 = hingeOnLeft ? lx0 + 0.1 * IN : lx1 - 0.1 * IN - strap;
      p.bevel(hw, hx0, hx0 + strap, at - 0.75 * IN, at + 0.75 * IN, proud, proud + 0.25 * IN, 0.12 * IN, { grain: 'x' });
    }
    const pullX = hingeOnLeft ? lx1 - board / 2 : lx0 + board / 2;
    const pullY = dy0 + dh * 0.36;
    p.eased(hw, pullX - 0.5 * IN, pullX + 0.5 * IN, pullY, pullY + 9 * IN, proud + 0.6 * IN, proud + 1.3 * IN, 0.2 * IN);
    for (const at of [pullY + 1 * IN, pullY + 8 * IN]) {
      p.box(hw, pullX - 0.3 * IN, pullX + 0.3 * IN, at - 0.3 * IN, at + 0.3 * IN, proud, proud + 0.6 * IN);
    }
  }
}

/**
 * A wall taken out between two rooms: the framed opening, finished. Panel runs
 * through the thickness of the wall on both jambs and over the head (and under
 * a sill, where the opening is off the floor), so none of the framing shows and
 * the rooms' own panelling carries on, in line, to the corners of the opening.
 * Where the two come together at the face of a wall they make an OUTSIDE corner
 * (trimmed with the opening's trim); where jamb meets head, an INSIDE corner,
 * which gets a small cove.
 *
 * Built from the framed wall's side only (`through` is set on that copy). Wall
 * plane z = 0; the panelling of this room stands at z = 0..d, the other room's
 * at z = -through - d..-through.
 */
function addPassageReturns(p: Parts, o: OpeningShape): void {
  const through = o.through ?? 0;
  if (through <= 0) return;
  const d = getProfile('WCB').depthSize;
  const colour = parseInt((o.color ?? '#FFFFFF').replace('#', ''), 16);
  const panelFinish: Finish = { color: colour, roughness: 0.55 };
  const coveFinish: Finish = { color: colour, roughness: 0.5 };
  const z0 = -through;
  const z1 = d;
  const x0 = o.x;
  const x1 = o.x + o.width;
  const y0 = Math.max(o.y, 0.003); // (a jamb that stands on the floor must not share its plane)
  const y1 = o.y + o.height;
  if (x1 - x0 < 2 * d + 0.02 || y1 - y0 < 2 * d) return;
  const raised = o.y > 1e-6;
  // Jambs, full height; head and sill between them.
  p.box(panelFinish, x0, x0 + d, y0, y1, z0, z1);
  p.box(panelFinish, x1 - d, x1, y0, y1, z0, z1);
  p.box(panelFinish, x0 + d, x1 - d, y1 - d, y1, z0, z1);
  if (raised) p.box(panelFinish, x0 + d, x1 - d, o.y, o.y + d, z0, z1);
  // Inside corners.
  const c = 1.25 * IN;
  const corners: [number, number][] = [[x0 + d, x0 + d + c], [x1 - d - c, x1 - d]];
  for (const [cx0, cx1] of corners) {
    p.box(coveFinish, cx0, cx1, y1 - d - c, y1 - d, z0, z1);
    if (raised) p.box(coveFinish, cx0, cx1, o.y + d, o.y + d + c, z0, z1);
  }
}

/**
 * All the openings of one wall, in canonical front-face coordinates. The
 * caller applies the per-face transform, as it does for boards. `ceilingAt`
 * gives the ceiling's height over any point (x along the wall, z into the
 * room) — the garage hardware hangs from it: a number for a flat ceiling at
 * that height, a function under a sloped one, or nothing to hang no hardware.
 */
export function buildWallOpeningsGroup(openings: OpeningShape[], wallLength = Infinity, ceilingAt: number | CeilingAt = Infinity): THREE.Group {
  const ceiling: CeilingAt | null = typeof ceilingAt === 'function' ? ceilingAt : Number.isFinite(ceilingAt) ? () => ceilingAt : null;
  const root = new THREE.Group();
  root.name = 'openings';
  const parts = new Parts();
  for (const o of openings) {
    if (o.width <= 0 || o.height <= 0) continue;
    if (o.kind === 'passage') { addPassageReturns(parts, o); continue; } // a gap in the wall: nothing to hang in it
    if (o.kind === 'window') addWindow(parts, o, root);
    else if (o.kind === 'door') addSwingingDoor(parts, root, o);
    else addGarageDoor(parts, root, o, wallLength, ceiling);
  }
  parts.into(root);
  return root;
}
